import React from 'react';
import { Button, Dropdown, Select } from 'tea-component';
import { useIntl } from 'umi';
import BasicImage from '@home/components/BasicImage';
import AgentModeDefaultIcon from '../../../svg/agent-mode-default-icon.svg';
import SkillsDefaultIcon from '@/pages/aigc/assets/svg/skills-default-icon.svg';
import { FILE_TYPE_OPTIONS, TIME_RANGE_OPTIONS } from '../../constants';
import type { TFileLibrarySource, TFileLibraryTimeRange } from '../../types';
import type { IAgentData } from '../../../AgentCollection/types';
import SourceSelect from '../SourceSelect';

/** 筛选浮层 */
const FilterPanel: React.FC<{
  fileType: string;
  timeRange: TFileLibraryTimeRange;
  source: TFileLibrarySource;
  agents: IAgentData[];
  agentsLoading?: boolean;
  onFileTypeChange: (value: string) => void;
  onTimeRangeChange: (value: TFileLibraryTimeRange) => void;
  onSourceChange: (value: TFileLibrarySource) => void;
  onClear: () => void;
}> = ({
  fileType,
  timeRange,
  source,
  agents,
  agentsLoading,
  onFileTypeChange,
  onTimeRangeChange,
  onSourceChange,
  onClear,
}) => {
  const { formatMessage } = useIntl();
  const pleaseSelect = formatMessage({
    id: 'dba_file_library_please_select',
  });
  const hasFilter = Boolean(fileType || timeRange || source);
  const sourceFallbackIcon =
    source?.kind === 'skill' ? SkillsDefaultIcon : AgentModeDefaultIcon;
  const sourceValueClass =
    source?.kind === 'skill'
      ? 'dba-file-library-filter__source-value dba-file-library-filter__source-value--skill'
      : 'dba-file-library-filter__source-value';

  const fileTypeOptions = FILE_TYPE_OPTIONS.map((item) => ({
    value: item.value,
    text: formatMessage({ id: item.labelId }),
  }));
  const timeOptions = TIME_RANGE_OPTIONS.map((item) => ({
    value: item.value,
    text: formatMessage({ id: item.labelId }),
  }));

  return (
    <div className="dba-file-library-filter">
      <div className="dba-file-library-filter__header">
        <span>{formatMessage({ id: 'dba_file_library_filter_title' })}</span>
        {hasFilter ? (
          <Button type="link" onClick={onClear}>
            {formatMessage({ id: 'dba_file_library_filter_clear' })}
          </Button>
        ) : null}
      </div>
      <div className="dba-file-library-filter__field">
        <div className="dba-file-library-filter__label">
          {formatMessage({ id: 'dba_file_library_filter_file_type' })}
        </div>
        <div className="dba-file-library-filter__control">
          <Select
            appearance="button"
            size="full"
            matchButtonWidth
            clearable
            placeholder={pleaseSelect}
            options={fileTypeOptions}
            value={fileType || undefined}
            onChange={(value) => onFileTypeChange(value || '')}
          />
        </div>
      </div>
      <div className="dba-file-library-filter__field">
        <div className="dba-file-library-filter__label">
          {formatMessage({ id: 'dba_file_library_filter_time' })}
        </div>
        <div className="dba-file-library-filter__control">
          <Select
            appearance="button"
            size="full"
            matchButtonWidth
            clearable
            placeholder={pleaseSelect}
            options={timeOptions}
            value={timeRange || undefined}
            onChange={(value) =>
              onTimeRangeChange((value as TFileLibraryTimeRange) || '')
            }
          />
        </div>
      </div>
      <div className="dba-file-library-filter__field dba-file-library-filter__field--source">
        <div className="dba-file-library-filter__label">
          {formatMessage({ id: 'dba_file_library_filter_source' })}
        </div>
        <div className="dba-file-library-filter__control">
          <Dropdown
            appearance="button"
            clickClose={false}
            clearable={Boolean(source)}
            placement="bottom-start"
            matchButtonWidth
            boxClassName="dba-file-library-source-box"
            onClear={() => onSourceChange(null)}
            button={
              source ? (
                <span className={sourceValueClass}>
                  <BasicImage
                    src={source.icon || sourceFallbackIcon}
                    errorSrc={sourceFallbackIcon}
                  />
                  {source.name}
                </span>
              ) : (
                <span className="dba-file-library-filter__placeholder">
                  {pleaseSelect}
                </span>
              )
            }
          >
            {(close) => (
              <SourceSelect
                value={source}
                agents={agents}
                agentsLoading={agentsLoading}
                onChange={(next) => {
                  onSourceChange(next);
                  close();
                }}
              />
            )}
          </Dropdown>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
